/*
 *  -----------------------------------------------------------------------
 *    <copyright file="attendees-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useRef, useState } from 'react';
import { orderBy } from '@progress/kendo-data-query';
import { getRiskReviewAttendeesGridColumns } from './attendees-grid.data';
import ErrorMessage from '@components-clean/core/error';
import { GridHeaderTitle } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import { Attendee } from '@interfaces/report-templates.interface';
import DeleteAttendeeDialog from '@pages-clean/risk-assessment/monitoring-toolkit/manage-reviews-tab/dialogs/delete-attendee-dialog';
import ManageAttendeeDialog from '@pages-clean/risk-assessment/monitoring-toolkit/manage-reviews-tab/manage-attendee-dialog';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { useGetAttendeesList } from '@apis/bff-api/report-templates';

export interface AttendeesGridProps {
    id: string;
    studyNumber: string;
    chipHeaders: GridHeaderTitle[];
    selectedChipHeader: number;
    onChipHeaderSelected: (chipHeader: number) => void;
}

const AttendeesGrid = ({
    id: studyId,
    studyNumber,
    chipHeaders,
    selectedChipHeader,
    onChipHeaderSelected,
}: AttendeesGridProps) => {
    const { t } = useTranslation();
    const user = useAuthorization();

    const canEdit = !!user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const { attendeesData, mutateAttendees, loadingAttendeesFailed, isLoadingAttendees } = useGetAttendeesList(
        studyId,
        false,
    );

    const [currentAttendee, setCurrentAttendee] = useState<Attendee | null>(null);
    const manageAttendeeDialog = useBoolean(false);
    const deleteAttendeeDialog = useBoolean(false);

    const addActionRef = useRef<boolean>(false);

    if (loadingAttendeesFailed && !isLoadingAttendees) return <ErrorMessage />;

    const attendees = attendeesData
        ? orderBy(attendeesData, [
              { field: 'active', dir: 'desc' },
              { field: 'lastName', dir: 'asc' },
          ])
        : undefined;

    const onSubmit = async (attendeeId: string | undefined) => {
        manageAttendeeDialog.setFalse();
        if (attendeeId) {
            const attendeesResult = await mutateAttendees();
            const selectedAttendee = attendeesResult?.find((attendee) => attendee.id === attendeeId);
            setCurrentAttendee(selectedAttendee ?? null);
            addActionRef.current = true;
        }
    };

    const onDeleteSubmit = async () => {
        deleteAttendeeDialog.setFalse();
        setCurrentAttendee(null);
        await mutateAttendees(undefined, { revalidate: true });
    };

    const onDeleteClose = () => {
        deleteAttendeeDialog.setFalse();
        setCurrentAttendee(null);
    };

    return (
        <>
            {manageAttendeeDialog.value && (
                <ManageAttendeeDialog
                    studyId={studyId}
                    currentAttendee={currentAttendee ?? undefined}
                    onSubmit={(attendeeId) => onSubmit(attendeeId)}
                    onClose={() => manageAttendeeDialog.setFalse()}
                />
            )}
            {deleteAttendeeDialog.value && !!currentAttendee && (
                <DeleteAttendeeDialog attendee={currentAttendee} onSubmit={onDeleteSubmit} onClose={onDeleteClose} />
            )}
            <Grid
                excludeHiddenRowMenuActions
                title={chipHeaders}
                onChipHeaderSelected={onChipHeaderSelected}
                selectedChipHeader={selectedChipHeader}
                selectedTitle={chipHeaders.find((header) => header.id === selectedChipHeader)}
                maxHeight="auto"
                defaultSelectedItem={addActionRef.current ? (currentAttendee ?? undefined) : undefined}
                testDataId="risk-reviews-attendees-grid"
                data={attendees}
                columns={getRiskReviewAttendeesGridColumns(t)}
                clearGridSelectedRows={!currentAttendee}
                loading={isLoadingAttendees}
                useLoadingOverlay
                showAddIcon
                canEdit={canEdit}
                addRecordTooltip={t('ra:addAttendee')}
                addCallback={() => {
                    setCurrentAttendee(null);
                    manageAttendeeDialog.setTrue();
                }}
                scrollable="virtual"
                dataItemKey="id"
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('ra:editAttendee'),
                        onClick: (dataItem) => {
                            setCurrentAttendee(dataItem as Attendee);
                            addActionRef.current = false;
                            manageAttendeeDialog.setTrue();
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'],
                    },
                    {
                        id: 2,
                        label: t('ra:deleteAttendee'),
                        onClick: (dataItem: unknown) => {
                            setCurrentAttendee(dataItem as Attendee);
                            deleteAttendeeDialog.setTrue();
                        },
                        icon: ['fas', 'trash-xmark'],
                        colorProp: 'Red',
                        disabled: !canEdit,
                    },
                ]}
                exportFilename={`Risk Review Attendees_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY')}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                persistentStorageKey={String(PersistentStorageKeys['manage_risk-reviews-attendees'])}
            />
        </>
    );
};

export default AttendeesGrid;
